<template>
  <div class="latex-menu-button">
    <el-tooltip content="插入LaTeX公式" placement="bottom">
      <svg 
        class="func-img" 
        @click="onClick"
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="#333333"
      >
        <path d="M2 3h4v2H4v2h2v2H4v2h2v2H2v-2h2V9H2V7h2V5H2V3zm6 0h2v2H8V3zm4 0h2v2h-2V3zm4 0h4v2h-2v2h2v2h-2v2h2v2h-4v-2h2V9h-2V7h2V5h-2V3z"/>
        <text x="12" y="15" text-anchor="middle" font-size="10" font-family="serif" font-weight="bold">∑</text>
      </svg>
    </el-tooltip>

    <!-- LaTeX 公式输入对话框 -->
    <el-dialog
      title="插入LaTeX数学公式"
      :visible.sync="dialogVisible"
      width="700px"
      :before-close="handleClose"
      class="latex-dialog"
      center
    >
      <div class="latex-input-container">
        <!-- 输入和预览区域 -->
        <div class="main-content">
          <div class="input-section">
            <div class="section-title">
              <i class="el-icon-edit"></i>
              输入LaTeX公式
            </div>
            <el-input
              v-model="formula"
              type="textarea"
              :rows="3"
              placeholder="例如：E = mc^2 或 \frac{a}{b} 或 \sqrt{x}"
              @input="updatePreview"
              class="formula-input"
            />
          </div>

          <div class="preview-section">
            <div class="section-title">
              <i class="el-icon-view"></i>
              预览效果
            </div>
            <div class="preview-container">
              <div 
                v-if="previewHtml" 
                class="latex-preview"
                v-html="previewHtml"
              ></div>
              <div v-else class="preview-placeholder">
                <i class="el-icon-picture-outline"></i>
                <span>公式预览将在这里显示</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 常用公式示例 -->
        <div class="examples-section">
          <div class="section-title">
            <i class="el-icon-collection"></i>
            常用公式示例
            <span class="combo-tip">（可点击多个公式进行组合）</span>
          </div>
          <div class="examples-grid">
            <div
              v-for="example in examples"
              :key="example.name"
              class="example-item"
              @click="useExample(example.formula)"
            >
              <div class="example-name">{{ example.name }}</div>
              <div class="example-formula" v-html="renderExample(example.formula)"></div>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="handleClose" size="medium">取消</el-button>
        <el-button 
          type="primary" 
          @click="insertFormula" 
          :disabled="!formula"
          size="medium"
          class="insert-btn"
        >
          <i class="el-icon-check"></i>
          插入公式
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'LatexMenuButton',
  props: ['isActive'],
  data() {
    return {
      dialogVisible: false,
      formula: '',
      displayMode: false,
      previewHtml: '',
      insertCallback: null,
      savedRange: null,
      examples: [
        { name: '分数', formula: '\\frac{a}{b}' },
        { name: '平方根', formula: '\\sqrt{x}' },
        { name: '上标', formula: 'x^2' },
        { name: '下标', formula: 'x_1' },
        { name: '求和', formula: '\\sum_{i=1}^{n} x_i' },
        { name: '积分', formula: '\\int_{a}^{b} f(x) dx' },
        { name: '极限', formula: '\\lim_{x \\to \\infty} f(x)' },
        { name: '矩阵', formula: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
        { name: '希腊字母', formula: '\\alpha \\beta \\gamma \\delta' },
        { name: '质能方程', formula: 'E = mc^2' },
        { name: '正弦', formula: '\\sin(x)' },
        { name: '余弦', formula: '\\cos(x)' },
        { name: '正切', formula: '\\tan(x)' },
        { name: '余切', formula: '\\cot(x)' },
        { name: '正割', formula: '\\sec(x)' },
        { name: '余割', formula: '\\csc(x)' }
      ]
    }
  },
  methods: {
    onClick() {
      // 保存当前编辑器的选择位置
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        this.savedRange = selection.getRangeAt(0).cloneRange()
      }
      
      // 显示LaTeX公式输入对话框
      this.showDialog()
      
      // 触发扩展的点击事件，获取插入回调函数
      this.$emit('click', (callback) => {
        // 保存回调函数，等用户在对话框中点击插入时使用
        this.insertCallback = callback
      })
    },

    showDialog() {
      this.dialogVisible = true
      this.formula = ''
      this.displayMode = false  // 固定为内联模式
      this.previewHtml = ''
    },

    handleClose() {
      this.dialogVisible = false
      this.formula = ''
      this.displayMode = false
      this.previewHtml = ''
    },

    updatePreview() {
      if (!this.formula.trim()) {
        this.previewHtml = ''
        return
      }

      try {
        this.previewHtml = katex.renderToString(this.formula, {
          throwOnError: false,
          displayMode: this.displayMode
        })
      } catch (error) {
        this.previewHtml = ''
      }
    },

    useExample(formula) {
      // 支持公式组合：如果已有公式，则追加而不是替换
      if (this.formula.trim()) {
        // 在现有公式后添加空格和新公式
        this.formula = this.formula + ' ' + formula
      } else {
        // 如果没有现有公式，直接设置
        this.formula = formula
      }
      this.updatePreview()
    },

    renderExample(formula) {
      try {
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: false
        })
      } catch (error) {
        return formula // 如果渲染失败，返回原始公式
      }
    },

    triggerInsert(latexData) {
      if (this.insertCallback) {
        // 传递LaTeX数据和保存的位置信息
        this.insertCallback({
          ...latexData,
          savedRange: this.savedRange
        })
      } else {
        console.error('LaTeX插入失败：没有找到插入回调函数')
      }
    },

    insertFormula() {
      if (!this.formula.trim()) {
        this.$message.warning('请输入LaTeX公式')
        return
      }

      try {
        // 渲染LaTeX公式为HTML
        const renderedHtml = katex.renderToString(this.formula, {
          throwOnError: true,
          displayMode: this.displayMode
        })

        // 创建包含LaTeX公式的HTML
        const latexHtml = this.displayMode 
          ? `<div class="latex-formula latex-block" data-formula="${this.formula}" data-display="true" contenteditable="false">${renderedHtml}</div><p></p>`
          : `<span class="latex-formula latex-inline" data-formula="${this.formula}" data-display="false" contenteditable="false">${renderedHtml}</span> `

        // 触发插入LaTeX公式
        this.triggerInsert({
          formula: this.formula,
          displayMode: this.displayMode,
          renderedHtml: renderedHtml
        })

        this.handleClose()
        this.$message.success('LaTeX公式插入成功')
      } catch (error) {
        console.error('LaTeX插入错误:', error)
        this.$message.error('LaTeX公式语法错误，请检查后重试')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.latex-menu-button {
  .func-img {
    width: 20px;
    height: 20px;
    cursor: pointer;
    margin-left: 10px;
    margin-top: 8px;
    opacity: 0.8;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 1;
    }
  }
}

.latex-dialog {
  :deep(.el-dialog) {
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }
  
  :deep(.el-dialog__header) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 12px 12px 0 0;
    padding: 20px 24px;
    
    .el-dialog__title {
      color: white;
      font-weight: 600;
      font-size: 18px;
    }
    
    .el-dialog__headerbtn {
      .el-dialog__close {
        color: white;
        font-size: 20px;
        
        &:hover {
          color: #f0f0f0;
        }
      }
    }
  }
  
  :deep(.el-dialog__body) {
    padding: 24px;
    background-color: #fafbfc;
  }
  
  :deep(.el-dialog__footer) {
    padding: 16px 24px 24px;
    background-color: #fafbfc;
    border-radius: 0 0 12px 12px;
  }
}

.latex-input-container {
  .main-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
  }
  
  .section-title {
    font-weight: 600;
    color: #2c3e50;
    font-size: 14px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    i {
      color: #667eea;
      font-size: 16px;
    }
    
    .combo-tip {
      font-size: 12px;
      color: #95a5a6;
      font-weight: normal;
      font-style: italic;
      margin-left: 8px;
    }
  }
  
  .input-section {
    .formula-input {
      :deep(.el-textarea__inner) {
        border-radius: 8px;
        border: 2px solid #e9ecef;
        font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
        font-size: 14px;
        line-height: 1.5;
        transition: all 0.3s ease;
        resize: vertical;
        
        &:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        
        &::placeholder {
          color: #95a5a6;
          font-style: italic;
        }
      }
    }
  }

  .preview-section {
    .preview-container {
      min-height: 100px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      padding: 16px;
      background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .latex-preview {
        font-size: 20px;
        line-height: 1.6;
        color: #2c3e50;
        
        :deep(.katex) {
          font-size: 1.2em !important;
          
          .katex-html {
            display: inline-block !important;
          }
          
          .katex-mathml {
            display: none !important;
          }
        }
      }
      
      .preview-placeholder {
        color: #95a5a6;
        font-style: italic;
        font-size: 14px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        
        i {
          font-size: 24px;
          opacity: 0.5;
        }
      }
    }
  }

  .examples-section {
    .examples-label {
      font-weight: 600;
      margin-bottom: 16px;
      color: #2c3e50;
      font-size: 14px;
    }
    
    .examples-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 12px;
      max-height: 200px;
      overflow-y: auto;
      
      &::-webkit-scrollbar {
        width: 6px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
        
        &:hover {
          background: #a8a8a8;
        }
      }
      
      .example-item {
        border: 1px solid #e9ecef;
        border-radius: 6px;
        padding: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        background: white;
        text-align: center;
        
        &:hover {
          border-color: #667eea;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
          transform: translateY(-1px);
          
        }
        
        .example-name {
          font-size: 11px;
          color: #667eea;
          margin-bottom: 4px;
          font-weight: 500;
        }
        
        .example-formula {
          font-size: 12px;
          color: #2c3e50;
          background-color: #f8f9fa;
          padding: 4px;
          border-radius: 3px;
          min-height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          
          :deep(.katex) {
            font-size: 0.9em !important;
            
            .katex-html {
              display: inline-block !important;
            }
            
            .katex-mathml {
              display: none !important;
            }
          }
        }
      }
    }
  }
}

.dialog-footer {
  text-align: right;
  
  .el-button {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    
    &.insert-btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      
      &:hover {
        background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }
      
      &:disabled {
        background: #c0c4cc;
        transform: none;
        box-shadow: none;
      }
      
      i {
        margin-right: 4px;
      }
    }
  }
}
</style>
